import React, { useState } from 'react';
import { RecordingState } from '@/services/recordingEngine';
import { AudioMeter } from '@/components/AudioMeter';
import { formatDuration, formatFileSize } from '@/utils/helpers';
import {
  Square,
  Pause,
  Play,
  Radio,
  HardDrive,
  Activity,
  AlertCircle,
  Clock,
  Sparkles,
  Maximize2,
  Minimize2,
  Minimize,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  Monitor,
  X
} from 'lucide-react';

interface ActiveRecordingScreenProps {
  recordingState: RecordingState;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  stream: MediaStream | null;
  availableDiskSpaceGb: number;
  aspectRatio?: '16:9' | '9:16' | '1:1';
  onToggleScreenShare?: () => void;
  isCameraMode?: boolean;
}

export const ActiveRecordingScreen: React.FC<ActiveRecordingScreenProps> = ({
  recordingState,
  onPause,
  onResume,
  onStop,
  stream,
  availableDiskSpaceGb,
  aspectRatio = '16:9',
  onToggleScreenShare,
  isCameraMode = false
}) => {
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMiniBarMode, setIsMiniBarMode] = useState(false);

  const toggleFullscreen = async () => {
    if (window.electronAPI && typeof window.electronAPI.setFullScreen === 'function') {
      const next = await window.electronAPI.setFullScreen();
      setIsFullscreen(next);
    } else {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen().catch(() => {});
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen().catch(() => {});
        setIsFullscreen(false);
      }
    }
  };

  const handleMinimizeToDesktop = () => {
    if (window.electronAPI && typeof window.electronAPI.minimize === 'function') {
      window.electronAPI.minimize();
    }
  };

  /* MINI-BAR FLOATING OVERLAY MODE (Great for Single-Screen meeting recording) */
  if (isMiniBarMode) {
    return (
      <div className="flex-1 flex flex-col items-center justify-start p-4 ambient-gradient select-none">
        <div className="w-full max-w-xl p-3 bg-white/95 dark:bg-brand-surface/95 rounded-2xl border border-brand-border shadow-elevated backdrop-blur-xl flex items-center justify-between gap-4 transition-all animate-in fade-in slide-in-from-top-4">
          {/* Status & Timer */}
          <div className="flex items-center gap-2.5">
            <span className="w-3 h-3 rounded-full bg-brand-recording recording-pulse-ring shrink-0" />
            <div className="font-mono text-xl font-bold text-brand-text tabular-nums">
              {formatDuration(recordingState.durationSec)}
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-recording/10 text-brand-recording font-semibold">
              {recordingState.isPaused ? 'PAUSED' : 'REC'}
            </span>
          </div>

          {/* Compact Audio Visualizer */}
          <div className="w-32 hidden sm:block">
            <AudioMeter showLabels={false} compact={true} systemAudioEnabled={true} />
          </div>

          {/* Quick Controls */}
          <div className="flex items-center gap-2">
            {recordingState.isPaused ? (
              <button
                onClick={onResume}
                className="p-2 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white transition-colors"
                title="Resume Recording"
              >
                <Play className="w-4 h-4 fill-white" />
              </button>
            ) : (
              <button
                onClick={onPause}
                className="p-2 rounded-xl bg-brand-elevated hover:bg-slate-200 text-brand-text transition-colors"
                title="Pause Recording"
              >
                <Pause className="w-4 h-4" />
              </button>
            )}

            {/* Share Screen Toggle (Mini Bar) */}
            {onToggleScreenShare && isCameraMode && (
              <button
                onClick={onToggleScreenShare}
                className={`p-2 rounded-xl text-white transition-all cursor-pointer ${
                  recordingState.isScreenSharing
                    ? 'bg-rose-500 hover:bg-rose-600 animate-pulse'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
                title={recordingState.isScreenSharing ? 'Stop sharing screen' : 'Share screen'}
              >
                {recordingState.isScreenSharing ? <X className="w-4 h-4" /> : <Monitor className="w-4 h-4" />}
              </button>
            )}

            <button
              onClick={onStop}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-recording hover:bg-brand-recording/90 text-white font-bold text-xs shadow-md transition-all active:scale-95 cursor-pointer"
              title="Stop Recording"
            >
              <Square className="w-3.5 h-3.5 fill-white" />
              <span>Stop Recording</span>
            </button>

            {/* Expand Back to Studio */}
            <button
              onClick={() => setIsMiniBarMode(false)}
              className="p-2 rounded-xl bg-brand-elevated hover:bg-slate-200 text-brand-muted hover:text-brand-text transition-colors"
              title="Expand to Full Studio"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        </div>

        <p className="text-[11px] text-brand-muted mt-3">
          Mini-Bar Mode: MeetSplit AI is compact so your meeting window stays visible.
        </p>
      </div>
    );
  }

  /* FULL STUDIO MODE */
  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-8 flex flex-col justify-between items-center ambient-gradient select-none">
      {/* Top Banner Status & Window Mode Controls */}
      <div className="w-full max-w-4xl flex items-center justify-between pb-4 border-b border-brand-border/60">
        <div className="flex items-center gap-3">
          <div className="relative flex items-center justify-center">
            <span className="w-4 h-4 rounded-full bg-brand-recording recording-pulse-ring" />
            <span className="absolute w-2 h-2 rounded-full bg-white" />
          </div>

          <div>
            <h2 className="text-sm font-bold text-brand-text flex items-center gap-2">
              {recordingState.isPaused ? 'Recording Paused' : 'Recording Live'}
              <span className="text-xs px-2 py-0.5 rounded-full bg-brand-recording/10 text-brand-recording border border-brand-recording/20 font-mono">
                LIVE
              </span>
            </h2>
            <p className="text-xs text-brand-muted truncate max-w-md">
              Source: {recordingState.sourceName}
            </p>
          </div>
        </div>

        {/* Fullscreen & Mode Toggles */}
        <div className="flex items-center gap-2">
          {/* Mini-Bar Mode button */}
          <button
            onClick={() => setIsMiniBarMode(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-surface hover:bg-brand-elevated border border-brand-border text-xs text-brand-muted hover:text-brand-text transition-colors"
            title="Collapse into compact floating bar so your meeting window stays visible"
          >
            <ChevronUp className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Mini-Bar Mode</span>
          </button>

          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullscreen}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-colors ${
              isFullscreen
                ? 'bg-brand-accent text-white border-brand-accent shadow-sm'
                : 'bg-brand-surface hover:bg-brand-elevated border border-brand-border text-brand-text'
            }`}
            title="Toggle Fullscreen"
          >
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            <span>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</span>
          </button>
        </div>
      </div>

      {/* CENTER: Main Timer & Video Feed / Preview Container */}
      <div className="w-full max-w-4xl py-4 flex flex-col items-center justify-center space-y-5">
        {/* Large Tabular Timer Display */}
        <div className="text-center space-y-1">
          <div className="font-mono text-5xl md:text-7xl font-extrabold tracking-wider text-brand-text tabular-nums drop-shadow-sm">
            {formatDuration(recordingState.durationSec)}
          </div>
          <div className="flex items-center justify-center gap-3 text-xs text-brand-muted font-medium">
            <span>Duration</span>
            <span>•</span>
            <span className="font-mono text-brand-secondary font-semibold">{formatFileSize(recordingState.recordedBytes)} Written</span>
            <span>•</span>
            <span className="font-mono text-brand-muted">{recordingState.fps} FPS</span>
          </div>
        </div>

        {/* Live Audio Meters Panel */}
        <div className="w-full max-w-lg p-3 bg-white/90 dark:bg-brand-surface/80 rounded-2xl border border-brand-border shadow-card backdrop-blur-md">
          <AudioMeter showLabels={true} systemAudioEnabled={true} />
        </div>

        {/* Live Preview Container - Adapts to 9:16 phone mockup if Shorts mode */}
        {stream && (
          <div
            className={`relative bg-black/85 overflow-hidden border shadow-elevated flex items-center justify-center transition-all ${
              aspectRatio === '9:16'
                ? 'w-[200px] aspect-[9/16] rounded-3xl border-4 border-indigo-500/80 ring-4 ring-indigo-500/20 shadow-2xl'
                : 'w-full max-w-xl aspect-video rounded-2xl border-brand-border'
            }`}
          >
            {/* Top Smartphone notch if in 9:16 mode */}
            {aspectRatio === '9:16' && (
              <div className="absolute top-2 w-14 h-2.5 bg-slate-850 rounded-full z-20 pointer-events-none shadow-sm" />
            )}

            <video
              ref={(videoEl) => {
                if (videoEl && videoEl.srcObject !== stream) {
                  videoEl.srcObject = stream;
                  videoEl.play().catch(() => {});
                }
              }}
              muted
              playsInline
              className={`w-full h-full pointer-events-none ${
                aspectRatio === '9:16' ? 'object-cover' : 'object-contain'
              }`}
            />

            <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/75 backdrop-blur-md text-[10px] text-white/90 font-medium z-10">
              <span className="w-2 h-2 rounded-full bg-brand-recording animate-pulse" />
              <span>
                {recordingState.isScreenSharing || recordingState.sourceName.includes('Circle Camera')
                  ? '🖥️ Screen + ⭕ Circle Camera Live'
                  : aspectRatio === '9:16'
                  ? '9:16 Shorts Live'
                  : isCameraMode
                  ? '📹 Full Solo Camera Live'
                  : 'Live Monitor Stream'}
              </span>
            </div>

            {aspectRatio === '9:16' && (
              <div className="absolute bottom-2.5 right-2.5 px-2 py-0.5 rounded bg-black/75 backdrop-blur-md text-[9px] text-emerald-400 font-mono font-bold z-10">
                1080x1920
              </div>
            )}
          </div>
        )}
      </div>

      {/* BOTTOM: Recording Studio Controls Bar */}
      <div className="w-full max-w-xl p-3.5 bg-white/95 dark:bg-brand-surface/90 rounded-3xl border border-brand-border shadow-elevated backdrop-blur-xl flex items-center justify-center gap-4">
        {/* Pause / Resume Button */}
        {recordingState.isPaused ? (
          <button
            onClick={onResume}
            className="flex items-center gap-2 px-6 py-2.5 rounded-2xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-bold transition-all shadow-glow active:scale-95"
          >
            <Play className="w-4 h-4 fill-white" />
            <span>Resume</span>
          </button>
        ) : (
          <button
            onClick={onPause}
            className="flex items-center gap-2 px-6 py-2.5 rounded-2xl bg-brand-elevated hover:bg-slate-200 dark:hover:bg-brand-elevated/80 text-brand-text border border-brand-border text-xs font-semibold transition-all active:scale-95"
          >
            <Pause className="w-4 h-4" />
            <span>Pause</span>
          </button>
        )}

        {/* Dynamic Screen Share Button */}
        {onToggleScreenShare && isCameraMode && (
          <button
            onClick={onToggleScreenShare}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-bold transition-all shadow-md active:scale-95 cursor-pointer ${
              recordingState.isScreenSharing
                ? 'bg-rose-500 hover:bg-rose-600 text-white animate-pulse'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
            title={
              recordingState.isScreenSharing
                ? 'Stop sharing screen and return to full camera'
                : 'Share screen (camera docks into circle in bottom-right)'
            }
          >
            {recordingState.isScreenSharing ? (
              <>
                <X className="w-4 h-4" />
                <span>Stop Sharing</span>
              </>
            ) : (
              <>
                <Monitor className="w-4 h-4" />
                <span>Share Screen</span>
              </>
            )}
          </button>
        )}

        {/* Stop Recording Button */}
        <button
          onClick={onStop}
          className="flex items-center gap-2 px-8 py-3 rounded-2xl bg-brand-recording hover:bg-brand-recording/90 text-white text-sm font-bold transition-all shadow-recording-glow active:scale-95 cursor-pointer"
          title="Stop recording and immediately separate participants"
        >
          <Square className="w-4 h-4 fill-white" />
          <span>Stop Recording</span>
        </button>
      </div>

      {/* Stop Confirmation Modal */}
      {showStopConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="max-w-md w-full bg-white dark:bg-brand-surface border border-brand-border rounded-3xl p-6 shadow-elevated space-y-4">
            <div className="flex items-center gap-3 text-brand-recording">
              <AlertCircle className="w-6 h-6" />
              <h3 className="font-bold text-base text-brand-text">Finish Screen Recording?</h3>
            </div>

            <p className="text-xs text-brand-muted leading-relaxed">
              Recording will finalize and save to your local folder ({formatDuration(recordingState.durationSec)} total duration).
              MeetSplit AI will immediately initiate offline participant tile detection.
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setShowStopConfirm(false)}
                className="px-4 py-2 rounded-xl text-xs font-medium text-brand-muted hover:text-brand-text bg-brand-elevated border border-brand-border transition-colors"
              >
                Continue Recording
              </button>
              <button
                onClick={() => {
                  setShowStopConfirm(false);
                  onStop();
                }}
                className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-brand-recording hover:bg-brand-recording/90 shadow-recording-glow transition-all"
              >
                Yes, Stop & Separate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
